/**
 * CookieConsentBanner - GDPR-compliant cookie consent UI (Prior Blocking).
 * GA4 and Meta Pixel do not load until the user clicks "Accept all".
 * Three equal buttons: "Accept all", "Essential only", "Settings".
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { consentService } from '../services/consentService';
import Button from '../../ui/components/Button';
import { FormCheckbox } from '../../ui';
import './CookieConsentBanner.css';

const COOKIE_CONSENT_OPEN_SETTINGS_EVENT = 'cookie-consent:openSettings';

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [reopenSettings, setReopenSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    setVisible(!consentService.hasResponded());
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      setAnalyticsEnabled(consentService.getConsent() === true);
      setReopenSettings(true);
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener(COOKIE_CONSENT_OPEN_SETTINGS_EVENT, handleOpenSettings);
    return () => window.removeEventListener(COOKIE_CONSENT_OPEN_SETTINGS_EVENT, handleOpenSettings);
  }, []);

  const handleAcceptAll = () => {
    consentService.setConsent(true);
    setVisible(false);
    setReopenSettings(false);
  };

  const handleEssentialOnly = () => {
    consentService.setConsent(false);
    setVisible(false);
    setReopenSettings(false);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleSavePreferences = () => {
    consentService.setConsent(analyticsEnabled);
    setShowSettings(false);
    setVisible(false);
    setReopenSettings(false);
  };

  const handleCancelSettings = () => {
    setShowSettings(false);
    setReopenSettings(false);
    if (consentService.hasResponded()) {
      setVisible(false);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="cookie-consent-banner" role="dialog" aria-labelledby="cookie-consent-title" aria-describedby="cookie-consent-message">
      <div className="cookie-consent-banner__content">
        <h2 id="cookie-consent-title" className="cookie-consent-banner__title">
          {t('cookieConsent.title')}
        </h2>
        <p id="cookie-consent-message" className="cookie-consent-banner__message">
          {t('cookieConsent.message')}{' '}
          <Link to="/static/privacy" className="cookie-consent-banner__link">
            {t('cookieConsent.privacyLink')}
          </Link>
          .
        </p>

        {!showSettings ? (
          <div className="cookie-consent-banner__actions">
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={handleAcceptAll}
              className="cookie-consent-banner__btn"
            >
              {t('cookieConsent.acceptAll')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={handleEssentialOnly}
              className="cookie-consent-banner__btn"
            >
              {t('cookieConsent.essentialOnly')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={handleOpenSettings}
              className="cookie-consent-banner__btn"
            >
              {t('cookieConsent.settings')}
            </Button>
          </div>
        ) : (
          <div className="cookie-consent-banner__settings">
            <h3 className="cookie-consent-banner__settings-title">{t('cookieConsent.settingsTitle')}</h3>
            <div className="cookie-consent-banner__setting-item cookie-consent-banner__setting-item--disabled">
              <FormCheckbox
                checked={true}
                disabled={true}
                label={t('cookieConsent.necessaryCookies')}
              />
              <p className="cookie-consent-banner__setting-desc">{t('cookieConsent.necessaryCookiesDesc')}</p>
            </div>
            <div className="cookie-consent-banner__setting-item">
              <FormCheckbox
                checked={analyticsEnabled}
                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                label={t('cookieConsent.analyticsMarketingCookies')}
              />
              <p className="cookie-consent-banner__setting-desc">{t('cookieConsent.analyticsMarketingDesc')}</p>
            </div>
            <div className="cookie-consent-banner__settings-actions">
              <Button
                type="button"
                variant="primary"
                size="medium"
                onClick={handleSavePreferences}
                className="cookie-consent-banner__btn"
              >
                {t('cookieConsent.savePreferences')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="medium"
                onClick={handleCancelSettings}
                className="cookie-consent-banner__btn"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
